import { AppStore, SnackbarErrorAction, SnackbarInfoAction } from '@alfresco/aca-shared/store';
import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { SmtActionsService } from '../../services/actions.service';
import { formatDate } from '@angular/common';
import { ExtensionConfig, ExtensionService } from '@alfresco/adf-extensions';
import { CustomListQueryBuilderService } from '../../services/custom-list-query-builder.service';
import { TranslationService } from '@alfresco/adf-core';
import { CustomDataColumn } from '../../models/custom-viewer-config';

interface PropertyData {
  name: string;
  dataListName: string;
}

@Component({
  templateUrl: './excel-export.dialog.html',
  styleUrls: ['./excel-export.dialog.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ExcelExportDialog implements OnInit, OnDestroy {
  onDestroy$: Subject<boolean> = new Subject<boolean>();

  downloading: boolean = false;

  /** Emitted when the set access point is successfully set
   */
  @Output()
  success: EventEmitter<any> = new EventEmitter<any>();

  config: ExtensionConfig;

  constructor(
    private store: Store<AppStore>,
    private extensionService: ExtensionService,
    private dialog: MatDialogRef<ExcelExportDialog>,
    private smtActionsService : SmtActionsService,
    private customListQueryBuilderService: CustomListQueryBuilderService,
    private translationService: TranslationService,
    protected extensions: ExtensionService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.extensionService.load().then((res) => this.config = res);
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  download(){
      this.downloading = true;

      const customQuery = this.getQuery();

      this.smtActionsService.exportAllDocsByQuery(customQuery).subscribe(
        response => {
          this.downloading = false;
          if(response.type.includes('text/plain')){
            this.store.dispatch(new SnackbarInfoAction('ALDEN.DIALOGS.EXCEL_EXPORT.BACKGROUND_TASK'));
            this.dialog.close();
          }else{
            this.dialog.close();
            var title = this.translationService.instant(this.customListQueryBuilderService.viewerConfig.title);
            var filename = formatDate(new Date(),"yyyyMMddhhmm","es")+"-"+title+".xlsx";

            const file = new File([response], filename, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
            const url= window.URL.createObjectURL(file);

            var linkToFile = document.createElement('a');
            linkToFile.download = filename;
            linkToFile.href = url;
            linkToFile.click();
          }
        },
        err => {
          this.downloading = false;
          const errText = err.message;
          const jsonErr = errText ? JSON.parse(errText) : null;
          if(jsonErr && jsonErr.err == 'Oversize'){
            this.store.dispatch(new SnackbarErrorAction(
              this.translationService.instant('ALDEN.DIALOGS.EXCEL_EXPORT.ERRORS.OVERSIZE_1')+ " " + jsonErr.maxNodes + " " +this.translationService.instant('ALDEN.DIALOGS.EXCEL_EXPORT.ERRORS.OVERSIZE_2')));
          }else{
            this.store.dispatch(new SnackbarErrorAction('ALDEN.DIALOGS.EXCEL_EXPORT.ERRORS.GENERIC'));
          }
          console.error(err);
        }
      );
  }

  private getQuery(): any {
    const currentQuery = this.customListQueryBuilderService.getCurrentQuery();

    let customQuery = {
      "query" : {
        "query" : "","language" : "afts"
      },
      "sort" : currentQuery.sort,
      "filterQueries" : [],
      "properties" : this.getProperties()
    };

    if(this.data.selection?.count > 0){
      const ids = this.data.selection.nodes.map(node => "@sys:node-uuid:"+node.entry.id);
      customQuery["query"]["query"] = ids.join(" OR ");
    }else{
      customQuery["query"]["query"] = currentQuery.query.query;
      customQuery["query"]["language"] = currentQuery.query.language;
      customQuery["filterQueries"] = currentQuery.filterQueries;
    }

    return customQuery;
  }

  private getProperties(): PropertyData[] {
    return this.customListQueryBuilderService.columns
                .filter(column => !column.onlyFilter && !column.exportDisabled && (!column.visible || column.visible == "true" || (column.visible != "false" && this.extensions.evaluateRule(column.visible))) && !column.isHidden)
                .map((column: CustomDataColumn) => ({
                    title: column.title ? this.translationService.instant(column.title) :  this.translationService.instant(column.search.title),
                    name: this.getField(column),
                    isCustom: column.exportCustom,
                    dataListName: column.dataListName ? column.dataListName : "",
                    isAspect: column.type === "aspect" || column.search.settings.aspectField,
                    template: column.exportTemplate ? column.exportTemplate : ""
                  }
                ));
  }

  getField(column: CustomDataColumn) : string {
    const settings = column.search.settings;
    if(settings) {
        const field = settings.field;
        if(field)
            return field;
    } else {
        const key = column.key.split('properties.');
        if(key.length === 2)
            return key[1];
    }
    return "";
  }

}
